import { Answer } from "./answer";
import { EvergreenCommunityListingAvailability, } from "./community-contact";
import { CommunityMaster } from "./community-master";
import { Contact } from "./contact";
import { File } from "./file";
import { parseEntityId } from "./id-parser";
import { Note } from "./note";
import { Photo } from "./photo";
import { DEFAULT_REGION } from "./sentinels";
import { toPhoneNumberE164 } from "./telecom";
export const SettingsCommunitySortTypes = ["name", "number", "recent", "rating"];
export const LicenseStatus = {
    active: "Active",
    closed: "Closed",
    notApplicable: "Licensing Not Applicable",
    onProbation: "On Probation or In Litigation",
    pending: "Pending",
};
export class Community {
    constructor() {
        this.name = "";
        this.address = "";
        this.city = "";
        this.state = "";
        this.zip = "";
        this.email = "";
        this.emailOptOut = false;
        this.mainPhone = toPhoneNumberE164("");
        this.faxNumber = toPhoneNumberE164("");
        this.website = "";
        this.virtualTourURL = "";
        this.thumbnailUrl = "";
        this.tenantId = null;
        this.summary = "";
        this.priceDetails = "";
        this.monthlyPrice = null;
        this.priceHighEnd = null;
        this.feeType = "";
        this.moveInDeposit = null;
        this.secondPersonFee = "";
        this.petDeposit = "";
        this.monthlyPriceLastModified = null;
        this.priceHighEndLastModified = null;
        this.depositPriceLastModified = null;
        this.pricingNotesLastModified = null;
        this.unitsOrCapacity = 0;
        this.caregiverRatio = null;
        this.rating = null;
        this.id = null;
        this.properties = {};
        this.notes = [];
        this.draftNote = "";
        this.contactEntities = [];
        this.primaryContactEntities = [];
        this.legacyPhotos = [];
        this.photoEntities = [];
        this.location = null;
        this.paymentType = "";
        this.paymentPercentage = "";
        this.paymentFee = "";
        this.contractStatus = "";
        this.contractIssuedDate = null;
        this.contractExpiresDate = null;
        this.contractNotes = "";
        this.licensedSinceDate = null;
        this.licenseStatus = null;
        this.licensee = null;
        this.contractRenewal = null;
        this.showInSearchResults = true;
        this.answers = [];
        this.gender = null;
        this.regions = [];
        this.loadedThumbnail = undefined;
        this.dateAdded = new Date();
        this.updatedAt = new Date();
        this.createdBy = "";
        this.updatedBy = "";
        this.communityMasterId = CommunityMaster.UnmergedDefaultId;
        this.communityMasterVersion = null;
        this.version = 0;
        this.county = null;
        this.regionId = null;
        this.licenses = [];
        this.evergreenCommunityId = null;
        this.availability = EvergreenCommunityListingAvailability.Unknown;
        this.isAvailabilityAllowed = false;
        this.pricing = null;
    }
    clone() {
        return Community.clone(this);
    }
    geocodeableRepresentation() {
        if (!this.address) {
            return null;
        }
        if (this.zip || (this.city && this.state)) {
            return [this.address, this.city, this.state, this.zip]
                .filter(x => x)
                .join(", ");
        }
        return null;
    }
    hasContract() {
        return (this.contractStatus === "pending" ||
            this.contractStatus === "oneOff" ||
            this.contractStatus === "active");
    }
    defaultIncludedStateLicenseStatus() {
        // this returns true if the facility's license status is a license status
        // that should be included in the main facility list results *by default*.
        // For now, we'll include everything except for "onProbation" and "closed"
        // facilities by default.
        return this.licenseStatus != "onProbation" && this.licenseStatus != "closed";
    }
    get formattedShortAddress() {
        return formatShortAddress(this);
    }
    static clone(source) {
        var theClone = new Community();
        theClone.name = source.name;
        theClone.summary = source.summary;
        theClone.priceDetails = source.priceDetails;
        theClone.address = source.address;
        theClone.city = source.city;
        theClone.state = source.state;
        theClone.zip = source.zip;
        theClone.mainPhone = toPhoneNumberE164(source.mainPhone);
        theClone.faxNumber = toPhoneNumberE164(source.faxNumber);
        theClone.virtualTourURL = source.virtualTourURL;
        theClone.email = source.email;
        theClone.emailOptOut = source.emailOptOut;
        theClone.website = source.website;
        theClone.thumbnailUrl = source.thumbnailUrl;
        theClone.tenantId = source.tenantId;
        theClone.monthlyPrice = source.monthlyPrice;
        theClone.priceHighEnd = source.priceHighEnd;
        theClone.feeType = source.feeType;
        theClone.moveInDeposit = source.moveInDeposit;
        theClone.secondPersonFee = source.secondPersonFee;
        theClone.petDeposit = source.petDeposit;
        theClone.monthlyPriceLastModified = source.monthlyPriceLastModified;
        theClone.priceHighEndLastModified = source.priceHighEndLastModified;
        theClone.depositPriceLastModified = source.depositPriceLastModified;
        theClone.pricingNotesLastModified = source.pricingNotesLastModified;
        theClone.unitsOrCapacity = source.unitsOrCapacity;
        theClone.caregiverRatio = source.caregiverRatio;
        theClone.rating = source.rating;
        theClone.id = source.id;
        theClone.properties = { ...source.properties };
        theClone.location = source.location
            ? { ...source.location }
            : null;
        theClone.notes = (source.notes || []).map((n) => Note.clone(n));
        theClone.draftNote = source.draftNote;
        theClone.paymentType = source.paymentType;
        theClone.paymentPercentage = source.paymentPercentage;
        theClone.paymentFee = source.paymentFee;
        theClone.contractStatus = source.contractStatus;
        theClone.contractIssuedDate = source.contractIssuedDate;
        theClone.contractExpiresDate = source.contractExpiresDate;
        theClone.contractNotes = source.contractNotes;
        theClone.contractRenewal = source.contractRenewal;
        theClone.licensedSinceDate = source.licensedSinceDate;
        theClone.licenseStatus = source.licenseStatus;
        theClone.licensee = source.licensee;
        theClone.showInSearchResults = source.showInSearchResults;
        theClone.contactEntities = (source.contactEntities || []).map(n => Contact.clone(n));
        theClone.primaryContactEntities = (source.primaryContactEntities || []).map(n => Contact.clone(n));
        theClone.answers = (source.answers || []).map(a => Answer.clone(a));
        theClone.gender = source.gender;
        theClone.loadedThumbnail = source.loadedThumbnail;
        theClone.regions = (source.regions || []).slice();
        if (theClone.regions.length === 0) {
            theClone.regions = [DEFAULT_REGION];
        }
        theClone.dateAdded = source.dateAdded ? new Date(source.dateAdded) : new Date();
        theClone.updatedAt = source.updatedAt ? new Date(source.updatedAt) : new Date();
        theClone.updatedBy = source.updatedBy;
        theClone.createdBy = source.createdBy;
        theClone.communityMasterId = source.communityMasterId;
        theClone.communityMasterVersion = source.communityMasterVersion;
        theClone.version = source.version || 0;
        theClone.regionId = source.regionId;
        theClone.licenses = (source.licenses || []).map(l => CommunityLicense.clone(l));
        theClone.county = source.county;
        theClone.evergreenCommunityId = source.evergreenCommunityId;
        theClone.availability = source.availability;
        theClone.isAvailabilityAllowed = source.isAvailabilityAllowed;
        theClone.pricing = source.pricing;
        return theClone;
    }
    static load(source) {
        var theClone = new Community();
        theClone.name = source.name;
        theClone.summary = source.summary;
        theClone.priceDetails = source.priceDetails;
        theClone.address = source.address;
        theClone.city = source.city;
        theClone.state = source.state;
        theClone.zip = source.zip;
        theClone.mainPhone = toPhoneNumberE164(source.mainPhone);
        theClone.faxNumber = toPhoneNumberE164(source.faxNumber);
        theClone.virtualTourURL = source.virtualTourURL;
        theClone.email = source.email;
        theClone.emailOptOut = source.emailOptOut;
        theClone.website = source.website;
        theClone.thumbnailUrl = source.thumbnailUrl;
        theClone.tenantId = source.tenantId;
        theClone.monthlyPrice = source.monthlyPrice;
        theClone.priceHighEnd = source.priceHighEnd;
        theClone.feeType = source.feeType;
        theClone.moveInDeposit = source.moveInDeposit;
        theClone.secondPersonFee = source.secondPersonFee;
        theClone.petDeposit = source.petDeposit;
        theClone.monthlyPriceLastModified = source.monthlyPriceLastModified;
        theClone.priceHighEndLastModified = source.priceHighEndLastModified;
        theClone.depositPriceLastModified = source.depositPriceLastModified;
        theClone.pricingNotesLastModified = source.pricingNotesLastModified;
        theClone.unitsOrCapacity = source.unitsOrCapacity;
        theClone.caregiverRatio = source.caregiverRatio;
        theClone.rating =
            source.rating === undefined || source.rating === null
                ? null
                : parseFloat(source.rating);
        theClone.id = source.id;
        theClone.properties = { ...source.properties };
        theClone.location = source.location ? { ...source.location } : null;
        theClone.notes = (source.notes || []).map((n) => Note.load(n));
        theClone.draftNote = source.draftNote;
        theClone.paymentType = source.paymentType;
        theClone.paymentPercentage = source.paymentPercentage;
        theClone.paymentFee = source.paymentFee;
        theClone.contractStatus = source.contractStatus;
        theClone.contractIssuedDate = source.contractIssuedDate;
        theClone.contractExpiresDate = source.contractExpiresDate;
        theClone.contractNotes = source.contractNotes;
        theClone.contractRenewal = source.contractRenewal;
        theClone.licensedSinceDate = source.licensedSinceDate;
        theClone.licenseStatus = source.licenseStatus;
        theClone.licensee = source.licensee;
        theClone.showInSearchResults = source.showInSearchResults;
        theClone.contactEntities = (source.contactEntities || []).map((n) => Contact.load(n));
        theClone.primaryContactEntities = (source.primaryContactEntities || []).map((n) => Contact.load(n));
        theClone.legacyPhotos = (source.legacyPhotos || []).map((p) => File.load(p));
        theClone.photoEntities = (source.photoEntities || []).map((p) => Photo.load(p));
        theClone.answers = (source.answersField || source.answers || []).map((a) => Answer.load(a));
        theClone.gender = source.gender;
        theClone.loadedThumbnail = source.loadedThumbnail;
        theClone.regions = (source.regions || []).slice();
        if (theClone.regions.length === 0) {
            theClone.regions = [DEFAULT_REGION];
        }
        theClone.dateAdded = source.dateAdded ? new Date(source.dateAdded) : new Date();
        theClone.updatedAt = source.updatedAt ? new Date(source.updatedAt) : new Date();
        theClone.updatedBy = source.updatedBy;
        theClone.createdBy = source.createdBy;
        theClone.communityMasterId = source.communityMasterId;
        theClone.communityMasterVersion = source.communityMasterVersion;
        theClone.version = source.version || 0;
        theClone.regionId = source.regionId;
        theClone.licenses = (source.licenses || []).map((l) => CommunityLicense.load(l));
        theClone.county = source.county;
        theClone.evergreenCommunityId = source.evergreenCommunityId;
        theClone.availability = source.availability;
        theClone.isAvailabilityAllowed = source.isAvailabilityAllowed;
        theClone.pricing = source.pricing;
        return theClone;
    }
}
export function formatShortAddress(fields) {
    let addressStr = "";
    if (fields.city && fields.city != "") {
        addressStr = fields.city;
    }
    if (fields.state && fields.state != "") {
        addressStr += ", " + fields.state;
    }
    if (fields.zip && fields.zip != "") {
        addressStr += " " + fields.zip;
    }
    return addressStr;
}
class CommunityLicense {
    constructor() {
        this.id = null;
        this.communityId = null;
        this.licenseNumber = "";
        this.href = "";
        this.licensee = "";
        this.status = "";
        this.licenseStartDate = "";
        this.licenseEndDate = "";
    }
    static clone(source) {
        const result = new CommunityLicense();
        result.id = source.id;
        result.communityId = source.communityId;
        result.licenseNumber = source.licenseNumber;
        result.href = source.href;
        result.licensee = source.licensee;
        result.status = source.status;
        result.licenseStartDate = source.licenseStartDate;
        result.licenseEndDate = source.licenseEndDate;
        return result;
    }
    static load(source) {
        const result = new CommunityLicense();
        if (typeof source === "object" && source) {
            const obj = source;
            result.id = parseEntityId(obj["id"]);
            result.communityId = parseEntityId(obj["communityId"]);
            result.licenseNumber =
                typeof obj["licenseNumber"] === "string" ? obj["licenseNumber"] : "";
            result.href = typeof obj["href"] === "string" ? obj["href"] : "";
            result.licensee = typeof obj["licensee"] === "string" ? obj["licensee"] : "";
            result.status = typeof obj["status"] === "string" ? obj["status"] : "";
            result.licenseStartDate =
                typeof obj["licenseStartDate"] === "string"
                    ? obj["licenseStartDate"]
                    : "";
            result.licenseEndDate =
                typeof obj["licenseEndDate"] === "string" ? obj["licenseEndDate"] : "";
        }
        return result;
    }
}
//# sourceMappingURL=community.js.map